/**
 * AutomatedTextsAdmin — when the system may text a driver by itself (#150).
 *
 * ⭐ Pete, 2026-08-20: *"Send it automatically but I need to see a history of
 * texts sent successfully or failed... As always, we need the logic for automated
 * texts including appt reminders in a config screen."*
 *
 * ⛔ THIS SCREEN ARMS SOMETHING. Every other config screen in the app changes what
 * a scheduler sees or what a form demands. This one decides whether messages go to
 * real drivers' phones with nobody pressing a button — so it says out loud what
 * will happen, shows what has already happened, and ships with everything off.
 *
 * ⚠️ IT IS NOT THE DOCK DOOR TEXT SCREEN. That one owns the WORDING; this owns
 * WHETHER WE SEND WITHOUT BEING ASKED. Same nouns, different decisions, different
 * blast radius — which is exactly why they are not one screen with one Save button.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, Loader2, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    ApiCallError, getAutomatedTextConfig, saveAutomatedTextConfig,
    clearAutomatedTextConfig, type AutomatedTextConfigDto, type AutomatedTextResponse,
} from '@/api/client';

interface Props {
    getToken: () => Promise<string>;
    facilities: { FacilityCode: string; DisplayName: string }[];
}

/** '' | '07:30' → minutes, or null. */
function toMinutes(hhmm: string): number | null {
    const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
    if (!m) return null;
    const h = Number(m[1]), mi = Number(m[2]);
    if (h > 23 || mi > 59) return null;
    return h * 60 + mi;
}

function toHhMm(minutes: number | null): string {
    if (minutes === null || minutes === undefined) return '';
    const h = Math.floor(minutes / 60), m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function AutomatedTextsAdmin({ getToken, facilities }: Props) {
    const [facilityCode, setFacilityCode] = useState(facilities[0]?.FacilityCode ?? '');
    const [data, setData] = useState<AutomatedTextResponse | null>(null);
    const [form, setForm] = useState<AutomatedTextConfigDto | null>(null);
    const [quietStart, setQuietStart] = useState('');
    const [quietEnd, setQuietEnd] = useState('');
    /** false = this facility has no row, so nothing is automated. */
    const [configured, setConfigured] = useState(false);

    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [note, setNote] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!facilityCode) return;
        setLoading(true); setError(null); setNote(null);
        try {
            const r = await getAutomatedTextConfig(await getToken(), facilityCode);
            setData(r);
            const c = r.config ?? r.shippedDefaults;
            setConfigured(r.config !== null);
            setForm({ ...c });
            setQuietStart(toHhMm(c.quietStartMinutes));
            setQuietEnd(toHhMm(c.quietEndMinutes));
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setLoading(false);
        }
    }, [facilityCode, getToken]);

    useEffect(() => { void load(); }, [load]);

    /**
     * ⛔ BOTH ENDS OR NEITHER, checked before the request. Half a window is not a
     * window; the server and the CHECK constraint both refuse it, and catching it
     * here means a clear sentence instead of a 400 with an issues array.
     */
    const quietProblem = (() => {
        const a = quietStart.trim(), b = quietEnd.trim();
        if (!a && !b) return null;
        if (!a || !b) return 'Set both ends of the quiet window, or clear both.';
        if (toMinutes(a) === null) return `"${a}" is not a time (use 22:00).`;
        if (toMinutes(b) === null) return `"${b}" is not a time (use 07:00).`;
        return null;
    })();

    async function save() {
        if (!form || quietProblem) return;
        setBusy(true); setError(null); setNote(null);
        try {
            const r = await saveAutomatedTextConfig(await getToken(), {
                facilityCode,
                ...form,
                quietStartMinutes: toMinutes(quietStart),
                quietEndMinutes: toMinutes(quietEnd),
            });
            // ⛔ AFTER load(), NOT BEFORE. `load()` opens with setNote(null), so
            // setting the note first and then reloading wipes the confirmation
            // before anybody can read it — the screen looked like Save did
            // nothing. ⚠️ THIS IS A REPEAT: the Directions screen had the same
            // bug and has a deploy guard for it ("the saved banner outlives the
            // reload"); that guard does not cover this screen, so the defect
            // walked straight back in on a new one.
            // ⭐ It matters more here than on a wording screen: this button is
            // what starts putting messages on real phones, and a Save that
            // appears to do nothing invites a second click.
            await load();
            setNote(r.message);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setBusy(false);
        }
    }

    async function clearAll() {
        setBusy(true); setError(null); setNote(null);
        try {
            const r = await clearAutomatedTextConfig(await getToken(), facilityCode);
            // Same ordering trap as save() above.
            await load();
            setNote(r.message);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setBusy(false);
        }
    }

    const set = (patch: Partial<AutomatedTextConfigDto>) =>
        setForm(f => (f ? { ...f, ...patch } : f));

    const anythingOn = !!form && (form.enableDoorAssignedText || form.enableReminderText);

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-base font-semibold">Automated texts</h3>
                <p className="mt-0.5 text-sm text-muted-foreground">
                    When the system texts a driver without anybody pressing a button.
                    Wording lives on the <strong>Dock Door Text</strong> screen; this is
                    only about whether we send unasked.
                </p>
            </div>

            {/* ⛔ THE MIGRATION WARNING COMES FIRST. */}
            {data && !data.migrationApplied && (
                <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <div>
                        <div className="font-medium">Migration 152 has not been applied</div>
                        <div className="mt-0.5 text-muted-foreground">
                            Nothing can be saved. ⭐ Nothing is being sent automatically
                            either — the read path fails closed on purpose, so a missing
                            table means silence rather than unauthorised messages. Run
                            <code className="mx-1">152_automated_text_config.sql</code>.
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <span>{error}</span>
                </div>
            )}

            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label>Facility</Label>
                    <Select value={facilityCode} onValueChange={setFacilityCode}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {facilities.map(f => (
                                <SelectItem key={f.FacilityCode} value={f.FacilityCode}>
                                    {f.DisplayName || f.FacilityCode}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* ⭐ WHAT HAS ACTUALLY HAPPENED. Pete asked to see sent-and-failed;
                    a screen with only a toggle cannot distinguish working automation
                    from silent automation. */}
                <div className="space-y-1.5">
                    <Label>Texts, last 7 days</Label>
                    <div className="flex h-9 items-center gap-3 rounded-md border px-3 text-sm">
                        {data?.recent ? (
                            <>
                                <span className="text-emerald-700">{data.recent.sent} sent</span>
                                <span className={data.recent.failed > 0 ? 'font-medium text-destructive' : 'text-muted-foreground'}>
                                    {data.recent.failed} failed
                                </span>
                            </>
                        ) : (
                            <span className="text-muted-foreground">not available</span>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
            ) : form && (
                <div className="space-y-3">
                    <div className="text-xs text-muted-foreground">
                        {configured
                            ? 'Configured for this facility.'
                            : '⭐ Nothing is automated here. These are the shipped defaults — saving creates a rule.'}
                    </div>

                    {/* ---------- the door text ---------- */}
                    <div className="space-y-2 rounded-md border p-3">
                        <label className="flex cursor-pointer items-start gap-3">
                            <input
                                type="checkbox" className="mt-0.5" disabled={busy}
                                checked={form.enableDoorAssignedText}
                                onChange={e => set({ enableDoorAssignedText: e.target.checked })}
                            />
                            <div>
                                <div className="text-sm font-medium">
                                    Text the driver his door the moment it is assigned
                                </div>
                                <div className="mt-0.5 text-xs text-muted-foreground">
                                    Keeps the promise the "no dock door yet — we will text it to
                                    you" message already makes. Only for drivers who have
                                    checked in.
                                </div>
                            </div>
                        </label>

                        <label className={`flex items-start gap-3 pl-7 ${form.enableDoorAssignedText ? 'cursor-pointer' : 'opacity-60'}`}>
                            <input
                                type="checkbox" className="mt-0.5"
                                disabled={busy || !form.enableDoorAssignedText}
                                checked={form.doorTextOnlyIfPromised}
                                onChange={e => set({ doorTextOnlyIfPromised: e.target.checked })}
                            />
                            <div>
                                <div className="text-sm">Only if a text was promised</div>
                                <div className="mt-0.5 text-xs text-muted-foreground">
                                    ⭐ Recommended. With this on, only a driver who received the
                                    "no door yet" message is texted. ⚠️ Off means every arrived
                                    driver is texted when a door is assigned — including one who
                                    can already see the door he was given on arrival.
                                </div>
                            </div>
                        </label>
                    </div>

                    {/* ---------- the reminder ---------- */}
                    <div className="space-y-2 rounded-md border p-3">
                        <label className="flex cursor-pointer items-start gap-3">
                            <input
                                type="checkbox" className="mt-0.5" disabled={busy}
                                checked={form.enableReminderText}
                                onChange={e => set({ enableReminderText: e.target.checked })}
                            />
                            <div className="text-sm font-medium">
                                Remind booked drivers before their appointment
                            </div>
                        </label>
                        {/* ⛔ THE "Hours ahead" BOX WAS REMOVED HERE (2026-08-21, #167).
                            `dock.ReminderSchedule` owns lead times now — per facility OR
                            company, several of them, each with its own direction filter
                            and wording. Leaving one box here would be a SECOND SOURCE OF
                            TRUTH for the same fact: two controls, one value, certain to
                            drift, and no way for a scheduler to tell which one won.
                            ⚠️ `dock.AutomatedTextConfig.ReminderHoursBefore` still exists
                            in the database and is now read by NOTHING — migration 154
                            copied every value into a schedule entry. */}
                        <div className={`pl-7 text-xs text-muted-foreground ${form.enableReminderText ? '' : 'opacity-60'}`}>
                            When they are reminded — lead times, wording and inbound/outbound —
                            lives on the <span className="font-medium">Reminder Schedule</span>{' '}
                            screen. A site can have several reminders, not just one.
                        </div>
                    </div>

                    {/* ---------- quiet hours ---------- */}
                    <div className="space-y-2 rounded-md border p-3">
                        <div className="text-sm font-medium">Quiet hours</div>
                        {/* ⛔ THE ASYMMETRY, STATED ON THE SCREEN. Somebody configuring
                            this will otherwise assume it silences everything, and be
                            surprised — or worse, rely on it to silence door texts. */}
                        <div className="text-xs text-muted-foreground">
                            ⚠️ Applies to <strong>reminders only</strong>. A reminder is ours to
                            schedule, so one landing at 3am is our arithmetic being careless. A
                            door text is not — the driver is in the lot waiting for it, and
                            holding it back to be polite would strand him.
                        </div>
                        <div className="flex items-center gap-2">
                            <Input
                                className="h-8 w-24" placeholder="22:00" disabled={busy}
                                value={quietStart} onChange={e => setQuietStart(e.target.value)}
                            />
                            <span className="text-xs text-muted-foreground">to</span>
                            <Input
                                className="h-8 w-24" placeholder="07:00" disabled={busy}
                                value={quietEnd} onChange={e => setQuietEnd(e.target.value)}
                            />
                            <span className="text-xs text-muted-foreground">
                                facility local; leave both blank for none
                            </span>
                        </div>
                        {quietProblem && <p className="text-xs text-destructive">{quietProblem}</p>}
                    </div>

                    {/* ---------- the runaway guard ---------- */}
                    <div className="flex items-center gap-2 rounded-md border p-3">
                        <Label htmlFor="max-t" className="text-sm">Most automatic texts per appointment</Label>
                        <Input
                            id="max-t" className="h-8 w-20" inputMode="numeric" disabled={busy}
                            value={String(form.maxAutoTextsPerAppointment)}
                            onChange={e => set({ maxAutoTextsPerAppointment: Number(e.target.value) || 1 })}
                        />
                        <span className="text-xs text-muted-foreground">
                            ⛔ The runaway guard — a door reassigned repeatedly, or a retry that
                            does not know it already succeeded, is how one driver gets forty texts.
                        </span>
                    </div>

                    {/* ⭐ SAYS WHAT IS ARMED, before you press Save. */}
                    {anythingOn && (
                        <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                            <div>
                                <div className="font-medium">This sends messages to real phones</div>
                                <ul className="mt-1 space-y-0.5 text-muted-foreground">
                                    {form.enableDoorAssignedText && (
                                        <li>
                                            {form.doorTextOnlyIfPromised
                                                ? 'A driver told "no door yet" will be texted when his door is assigned.'
                                                : '⚠️ ANY arrived driver will be texted when a door is assigned — promised or not.'}
                                        </li>
                                    )}
                                    {form.enableReminderText && (
                                        // ⚠️ NO LONGER QUOTES A LEAD TIME. It used to say
                                        // "reminded {n}h ahead" from this screen's own
                                        // field — which is now read by nothing, so the
                                        // sentence would have been confidently wrong.
                                        <li>
                                            Booked appointments will be reminded according to the
                                            facility&rsquo;s <span className="font-medium">Reminder
                                            Schedule</span> — check that screen to see exactly
                                            which reminders are armed and what they say.
                                        </li>
                                    )}
                                </ul>
                                <div className="mt-1.5 text-xs text-muted-foreground">
                                    Enable one facility, watch it for a day, then spread. A text
                                    sent by mistake cannot be recalled.
                                </div>
                            </div>
                        </div>
                    )}

                    {!anythingOn && (
                        <div className="flex gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                            <Info className="mt-0.5 h-4 w-4 shrink-0" />
                            Nothing is sent automatically. Schedulers text drivers by hand from
                            the board, exactly as they do today.
                        </div>
                    )}

                    {note && (
                        <div className="flex gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                            <span>{note}</span>
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-2">
                        <Button
                            variant="outline" size="sm" onClick={() => void clearAll()}
                            disabled={busy || !configured || !data?.migrationApplied}
                            title={configured ? 'Delete this rule — nothing automated here'
                                              : 'Nothing is configured for this facility'}
                        >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Turn everything off
                        </Button>
                        <Button
                            size="sm" onClick={() => void save()}
                            disabled={busy || !!quietProblem || !data?.migrationApplied}
                        >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <Save className="h-3.5 w-3.5" />}
                            Save
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
